import { WifiOff, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { cn } from '@/lib/utils';

export function NetworkStatusBanner() {
  const { isOnline, isSyncing, pendingCount, performSync } = useOfflineSync();

  // Don't show anything if online and no pending items
  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div
      style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      className={cn(
        'network-status-banner',
        'fixed left-4 right-4 md:left-auto md:right-4 md:w-auto z-50',
        'flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg',
        'transition-all duration-300 ease-out',
        // Mobile-optimized: larger padding and touch targets
        'min-h-[56px]',
        isOnline
          ? 'bg-card border border-border'
          : 'bg-warning-50 dark:bg-warning-700/50 border border-warning-200 dark:border-warning-700'
      )}
    >
      {/* Status Icon */}
      <div
        className={cn(
          'flex items-center justify-center h-10 w-10 rounded-lg shrink-0',
          isOnline
            ? 'bg-primary/10 text-primary'
            : 'bg-warning-100 dark:bg-warning-700 text-warning-600 dark:text-warning-400'
        )}
      >
        {isSyncing ? (
          <RefreshCw className="h-5 w-5 animate-spin" />
        ) : isOnline ? (
          <Cloud className="h-5 w-5" />
        ) : (
          <WifiOff className="h-5 w-5" />
        )}
      </div>

      {/* Status Text */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm font-medium',
            isOnline ? 'text-foreground' : 'text-warning-700 dark:text-warning-200'
          )}
        >
          {isSyncing
            ? 'Syncing...'
            : isOnline
            ? `${pendingCount} pending ${pendingCount === 1 ? 'entry' : 'entries'}`
            : 'You\'re offline'}
        </p>
        <p
          className={cn(
            'text-xs',
            isOnline ? 'text-muted-foreground' : 'text-warning-600 dark:text-warning-400'
          )}
        >
          {isSyncing
            ? 'Uploading offline entries'
            : isOnline
            ? 'Ready to sync'
            : 'Changes will sync when back online'}
        </p>
      </div>

      {/* Sync Button (only show when online with pending items) */}
      {isOnline && pendingCount > 0 && !isSyncing && (
        <Button
          size="sm"
          variant="outline"
          onClick={performSync}
          className="shrink-0 min-h-[44px] min-w-[44px] px-4"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Sync
        </Button>
      )}

      {/* Offline indicator */}
      {!isOnline && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-warning-100 dark:bg-warning-700 rounded-lg shrink-0">
          <CloudOff className="h-4 w-4 text-warning-600 dark:text-warning-400" />
          <span className="text-xs font-medium text-warning-700 dark:text-warning-300">
            Offline Mode
          </span>
        </div>
      )}
    </div>
  );
}
